/**
 * /app/$tenantSlug/me/assets — Me → Assets (asset slice A1).
 *
 * The employee's own equipment view: everything currently issued to ME,
 * with the receipt acknowledgment ("I received this") that makes the
 * register legally useful at recovery time. Server-scoped to the caller
 * (GET /me/assets — WHERE user_id = caller in SQL); the acknowledge
 * write additionally binds the assignment's user_id to the caller, so
 * nothing here can see or touch another person's assignments.
 *
 * Acknowledgment is one-way and once-only (guarded UPDATE); until it's
 * done the row shows a prominent "Confirm receipt" button, after it a
 * quiet acknowledged-on date. hr_core feature gate matches the register.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Package } from "lucide-react";
import { hrApi, toApiError } from "@/lib/api";
import { MY_ASSETS_KEY } from "@/hooks/use-hr";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { HrFeatureGate } from "@/components/hr/hr-feature-gate";
import { EmptyState } from "@/components/shared/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { fmtDate } from "@/lib/leave-format";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/assets")({
  component: MyAssetsRoute,
});


function MyAssetsRoute() {
  const qc = useQueryClient();

  const assetsQ = useQuery({
    queryKey: MY_ASSETS_KEY,
    queryFn: ({ signal }) => hrApi.listMyAssets(signal),
  });

  const ack = useMutation({
    mutationFn: (assignmentId: number) => hrApi.acknowledgeMyAsset(assignmentId),
    onSuccess: async () => {
      toast.success("Receipt confirmed.");
      await qc.invalidateQueries({ queryKey: MY_ASSETS_KEY });
    },
    onError: (err) => {
      toast.error(toApiError(err).message || "Could not confirm receipt.");
    },
  });

  const assets = assetsQ.data?.assets ?? [];

  return (
    <MeModuleShell
      title="Assets"
      description="Company equipment issued to you. Confirm receipt of anything newly assigned."
    >
      <HrFeatureGate>
        {assetsQ.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : assetsQ.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t load your assets</AlertTitle>
            <AlertDescription>
              {toApiError(assetsQ.error).message || "Please refresh and try again."}
            </AlertDescription>
          </Alert>
        ) : assets.length === 0 ? (
          <EmptyState
            icon={Package}
            title="Nothing Issued to You"
            description="When HR assigns you equipment — a laptop, a phone, an access card — it appears here."
          />
        ) : (
          <div className="space-y-3">
            {assets.map((a) => {
              const pendingAck = !a.acknowledged_at;
              return (
                <Card key={a.assignment_id}>
                  <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {a.asset_type_name}
                        </p>
                        {a.asset_tag && (
                          <Badge variant="outline" className="shrink-0 whitespace-nowrap font-mono text-xs">
                            {a.asset_tag}
                          </Badge>
                        )}
                        {pendingAck && (
                          <Badge variant="secondary" className="shrink-0 whitespace-nowrap">
                            Awaiting Your Confirmation
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Issued {fmtDate(a.assigned_at)}
                        {a.serial_number && <> · S/N {a.serial_number}</>}
                        {a.condition_out && <> · Condition at issue: {a.condition_out}</>}
                      </p>
                    </div>
                    {pendingAck ? (
                      <Button
                        size="sm"
                        disabled={ack.isPending}
                        onClick={() => ack.mutate(a.assignment_id)}
                        data-testid={`ack-asset-${a.assignment_id}`}
                      >
                        Confirm Receipt
                      </Button>
                    ) : (
                      <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        Receipt confirmed {fmtDate(a.acknowledged_at!)}
                      </span>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </HrFeatureGate>
    </MeModuleShell>
  );
}
